import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, Sparkles } from 'lucide-react';
import { getTabTourContent } from '../config/navRegistry';
import { API_BASE } from '../config/apiBase';
import { OverlayQueue, OVERLAY_PRIORITY } from '../utils/overlayQueue';

const API = API_BASE;
const isMobile = () => window.innerWidth < 768;

// Singleton cache to avoid redundant API calls
let _dismissedCache = null;
let _cacheUserId = null;

export function TabTour({ tabKey }) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const tourRef = useRef(null);
  const acquiredRef = useRef(false); // tracks whether this instance holds a queue slot

  useEffect(() => {
    if (!tabKey) return;
    let cancelled = false;

    const check = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const userId = JSON.parse(atob(token.split('.')[1])).user_id;
        if (_cacheUserId !== userId) { _dismissedCache = null; _cacheUserId = userId; }

        if (!_dismissedCache) {
          const res = await fetch(`${API}/api/user/tab-tours`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            _dismissedCache = data.tours || {};
          } else {
            _dismissedCache = {};
          }
        }

        if (cancelled) return;

        // Get content from registry (single source of truth)
        const content = getTabTourContent(tabKey);
        if (!content) return; // No tour content for this tab

        const existing = _dismissedCache[tabKey];
        // Show if: never dismissed, OR version changed
        if (!existing || !existing.dismissed || (content.version && existing.version < content.version)) {
          setDismissed(false);
          // Queue: tab tour waits for category onboarding AND main tour to finish first.
          const slotId = `tab_tour_${tabKey}`;
          await OverlayQueue.acquire(slotId, OVERLAY_PRIORITY.TAB_TOUR);
          if (cancelled) { OverlayQueue.release(slotId); return; }
          acquiredRef.current = true;
          setVisible(true);
        }
      } catch { /* silent */ }
    };

    check();
    return () => {
      cancelled = true;
      // If we were waiting for / holding a slot, release it so the queue moves on.
      OverlayQueue.release(`tab_tour_${tabKey}`);
      acquiredRef.current = false;
    };
  }, [tabKey]);

  const dismiss = useCallback(async () => {
    setVisible(false);
    setDismissed(true);
    const content = getTabTourContent(tabKey);
    const version = content?.version || 1;
    if (_dismissedCache) _dismissedCache[tabKey] = { dismissed: true, version };
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/user/tab-tours/${tabKey}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ version }),
      });
    } catch { /* silent */ }
    // Release queue so biometric (if mobile) can open next.
    OverlayQueue.release(`tab_tour_${tabKey}`);
    acquiredRef.current = false;
  }, [tabKey]);

  if (dismissed || !visible) return null;

  const content = getTabTourContent(tabKey);
  if (!content) { dismiss(); return null; }

  const mobile = isMobile();

  return (
    <div className="tab-tour-overlay" data-testid={`tab-tour-${tabKey}`} onClick={dismiss}>
      <div
        ref={tourRef}
        className={`tab-tour-popup ${mobile ? 'tab-tour-mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tab-tour-header">
          <Sparkles size={16} style={{ color: 'var(--sv-accent)' }} />
          <span className="tab-tour-title">{content.title}</span>
          <button className="tab-tour-close" onClick={dismiss} data-testid={`tab-tour-close-${tabKey}`}><X size={14} /></button>
        </div>
        <p className="tab-tour-desc">{content.desc}</p>
        <div className="tab-tour-tips">
          {content.tips.map((tip, i) => (
            <div key={i} className="tab-tour-tip">
              <ChevronRight size={12} style={{ color: 'var(--sv-accent)', flexShrink: 0 }} />
              <span>{tip}</span>
            </div>
          ))}
        </div>
        <button className="tab-tour-btn" onClick={dismiss} data-testid={`tab-tour-dismiss-${tabKey}`}>
          Entendido
        </button>
      </div>
    </div>
  );
}

export function useResetTabTours() {
  return async () => {
    _dismissedCache = null;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/user/tab-tours`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* silent */ }
    window.location.reload();
  };
}
